import * as React from "react";
import Component from "@reach/component-component";
import { Formik, FormikActions, FormikProps } from "formik";
import { Canceler } from "axios";
import { debounce, differenceBy } from "lodash";
import { toast, ToastId } from "react-toastify";
import { isEqual } from "lodash";
import { RouteProps, RouteComponentProps, withRouter } from "react-router";

import {
  IFormData,
  ILinkCreationRequestParameter,
  ILinkCreateState,
} from "./types";
import API from "src/services/api";
import Form from "./components/Form";
import LinkCreated from "./components/LinkCreated";
import Container from "src/components/Container";

class LinkCreate extends React.PureComponent<
  RouteProps & RouteComponentProps,
  ILinkCreateState
> {
  targetUrl = {
    checked: false,
    value: "",
  };
  validatingToast: ToastId;
  cancelTargetCheck: Canceler | null = null;
  cancelValidate: Canceler | null = null;
  initialState: ILinkCreateState = {
    duplicateId: null,
    targetCheckResult: null,
    isTargetChecking: false,
    isValidating: false,
    formData: {
      linkCreationRequestParameters: [],
    },
    isCreated: false,
    createdLink: null,
    confirmDuplicate: false,
  };
  state: ILinkCreateState = this.initialState;

  targetCheck = async () => {
    if (this.targetUrl.checked || !this.targetUrl.value) {
      return;
    }
    try {
      this.setState({ isTargetChecking: true });

      const { getData, cancel } = API.LINK.TARGET_CHECK.url(
        this.targetUrl.value
      );
      this.cancelTargetCheck = cancel;
      const data = await getData();
      this.cancelTargetCheck = null;

      this.setState({
        isTargetChecking: false,
        targetCheckResult: {
          success: data.httpCode === "ok" && data.redirects.length === 0,
          message: data.displayMessage,
        },
      });
      this.targetUrl.checked = true;
    } catch (error) {
      this.setState({
        isTargetChecking: false,
        targetCheckResult: {
          success: false,
          message: "Invalid URL",
        },
      });
      this.cancelTargetCheckRequest();
    }
  };
  debouncedTargetCheck = debounce(this.targetCheck, 300);

  cancelTargetCheckRequest = () => {
    if (this.cancelTargetCheck) {
      this.cancelTargetCheck();
      this.cancelTargetCheck = null;
    }
  };

  addItem = async (parameterId: number, value: string) => {
    const formData = { ...this.state.formData };
    const fieldIndex = formData.linkCreationRequestParameters.findIndex(
      (field) => field.id === parameterId
    );
    formData.linkCreationRequestParameters[fieldIndex].valueText = value;
    const data = await API.LINK.addItem(parameterId, formData);
    this.setState({
      formData: data,
    });
  };

  validateData = async (formData?: IFormData) => {
    try {
      this.setState({ isValidating: true });
      this.validatingToast = toast("Validating fields", {
        autoClose: false,
      });
      const { getData, cancel } = API.LINK.validate(
        formData || this.state.formData
      );
      this.cancelValidate = cancel;
      const data = await getData();
      this.cancelValidate = null;

      const hasErrors = data.linkCreationRequestParameters.find(
        (field: ILinkCreationRequestParameter) => field.hasError
      );
      toast.update(this.validatingToast, {
        render: hasErrors ? "Validated with errors" : "Validation success",
        type: hasErrors ? toast.TYPE.WARNING : toast.TYPE.SUCCESS,
        autoClose: 1000,
      });
      this.setState({
        isValidating: false,
        formData: data,
      });
    } catch (error) {
      this.cancelValidationRequest();
    }
  };
  debouncedValidateData = debounce(this.validateData, 300);

  cancelValidationRequest = () => {
    if (this.cancelValidate) {
      this.cancelValidate();
      this.cancelValidate = null;
      toast.dismiss(this.validatingToast);
    }
  };

  resetForm = () => {
    this.cancelValidationRequest();
    this.setState(this.initialState, this.validateData);
  };

  async componentDidMount() {
    const { location } = this.props;
    const locationState = location && location.state;
    if (
      locationState &&
      locationState.duplicateId &&
      typeof locationState.duplicateId === "number"
    ) {
      this.setState({
        duplicateId: locationState.duplicateVisibleId,
      });
      const data = await API.LINK.duplicate(locationState.duplicateId);
      this.setState({
        formData: data,
      });
    } else {
      this.validateData();
    }
  }

  componentWillUnmount() {
    this.cancelTargetCheckRequest();
    this.cancelValidationRequest();
  }

  jumpToArchive(uniqueLinkId: string) {
    const newLocation = {
      pathname: "/",
      state: { initialSearch: uniqueLinkId },
    };

    this.props.history.push(newLocation);
  }

  handleGoToArchive = (uniqueLinkId: string) => () => {
    this.jumpToArchive(uniqueLinkId);
  };

  onSubmit = async (values: IFormData, actions: FormikActions<IFormData>) => {
    try {
      const data = await API.LINK.create(
        this.state.formData,
        this.state.confirmDuplicate
      );
      actions.setSubmitting(false);
      if (data.duplicateLink) {
        if (data.allowDuplicateLinksViaWebInterface) {
          this.setState({
            confirmDuplicate: true,
          });
        } else {
          toast.error(
            <div>
              <div>
                Link already exists, change the values before trying again.
              </div>
              <div className="mb-3 mt-3">
                If you want to use this link:
                <button
                  type="button"
                  className="mt-1 leading-default border-white border rounded-lg transition-shadow focus:outline-none focus:shadow-outline hover:shadow-md px-8 py-2 sc-dnqmqq fPJnBs"
                  onClick={this.handleGoToArchive(
                    data.existDuplicatedLink.uniqueLinkId
                  )}
                >
                  Go to archive
                </button>
              </div>
            </div>,
            {
              autoClose: 5000,
            }
          );
        }
      }
      if (data.missingParameters) {
        toast.error(
          data.missingParameters
            .map((el: any) => `${el.name}: ${el.errorMessage}`)
            .join(", ")
        );
      }
      if (data.targetUrlNotAllowed) {
        toast.error(data.targetUrlNowAllowedErrorMessage);
      }
      if (data.created) {
        toast.success("Link created!");
        this.setState({
          isCreated: true,
          createdLink: {
            url: data.url,
            id: data.id,
            trackingCodeRedirectIssue: data.trackingCodeRedirectIssue,
            linkCreationNotifications: data.linkCreationNotifications,
            linkCreationResultAdditionalUrls:
              data.linkCreationResultAdditionalUrls,
          },
        });
      }
    } catch (error) {
      actions.setSubmitting(false);
      toast.error(error.message);
    }
  };

  updateValues = ({ prevProps, props }: any) => {
    const targetUrl = props.values.linkCreationRequestParameters.find(
      (parameter: ILinkCreationRequestParameter) => parameter.isTargetUrl
    );
    if (targetUrl && targetUrl.valueText !== this.targetUrl.value) {
      this.targetUrl = {
        checked: false,
        value: targetUrl.valueText,
      };
      this.cancelTargetCheckRequest();
      this.debouncedTargetCheck();
      if (targetUrl.valueText === "") {
        this.setState({
          targetCheckResult: null,
        });
      }
    }

    if (!isEqual(prevProps.initialValues, props.initialValues)) {
      return;
    }
    const prevParams = prevProps.values.linkCreationRequestParameters;
    const nextParams = props.values.linkCreationRequestParameters;
    if (prevParams.length && !isEqual(prevParams, nextParams)) {
      this.cancelValidationRequest();

      const diff = differenceBy(
        nextParams,
        prevParams,
        (param: ILinkCreationRequestParameter) =>
          param.selectedItemId + param.valueText
      );
      const changedParam = diff[0] as ILinkCreationRequestParameter;
      if (changedParam) {
        props.values.changedParameterId = changedParam ? changedParam.id : 0;
      }
      this.debouncedValidateData(props.values);
    }
  };

  duplicateLink = () => {
    this.setState((state) => ({
      isCreated: false,
      createdLink: null,
      duplicateId: state.createdLink && state.createdLink.id,
    }));
  };

  formRender = (props: FormikProps<IFormData>) => {
    return (
      <Form
        duplicateId={this.state.duplicateId}
        isValidatingData={this.state.isValidating}
        isLoading={
          this.state.formData.linkCreationRequestParameters.length === 0
        }
        onAdd={this.addItem}
        onReset={this.resetForm}
        isTargetChecking={this.state.isTargetChecking}
        targetCheckResult={this.state.targetCheckResult}
        confirmDuplicate={this.state.confirmDuplicate}
        {...props}
      >
        <Component
          values={props.values}
          initialValues={props.initialValues}
          didUpdate={this.updateValues}
        />
      </Form>
    );
  };

  render() {
    return (
      <Container>
        {this.state.isCreated && this.state.createdLink ? (
          <LinkCreated
            link={this.state.createdLink}
            onDuplicate={this.duplicateLink}
          />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={this.state.formData}
            onSubmit={this.onSubmit}
            render={this.formRender}
          />
        )}
      </Container>
    );
  }
}

export default withRouter(LinkCreate);



// WEBPACK FOOTER //
// ./src/views/LinkCreate/LinkCreate.tsx